import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { DownloadIcon, ImagesIcon, Trash2Icon } from "lucide-react";

import { pluralise } from "simplydo/core/helpers";
import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import {
  type OnCompleteParams,
  allowedImageTypesList,
} from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { cx, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent, CardHeader } from "simplydo/react/ui/card";
import { H4, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import { ImageChooser } from "components/lib/Choosers";
import { UploadedFilesTable } from "components/lib/UploadedFilesTable";

import type { ChooserGalleryImage } from "./types";

const imageHeight = 250;
const fullImageHeight = 365;

const GalleryImages = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
`;

const GalleryThumbnail = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  margin: 3px;
  object-fit: cover;
  cursor: pointer;

  &.is-active {
    border: 3px solid ${({ theme }) => theme.primaryColour};
  }
`;

const NoActiveImage = styled.div<{ $imageHeight: string; $imageWidth: string }>`
  border-radius: 10px;
  height: ${({ $imageHeight }) => $imageHeight};
  width: ${({ $imageWidth }) => $imageWidth};
  border: 1px solid lightgray;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 3px;
`;

type GalleryProps = {
  images: ChooserGalleryImage[];
  getFileUrl?: (file: ChooserGalleryImage) => string;
  onRemove?: (fileId: string) => void;
};

const Gallery = ({ images, getFileUrl, onRemove }: GalleryProps) => {
  const { t } = useTranslation();

  const [activeImage, setActiveImage] = useState<ChooserGalleryImage | null>(
    null,
  );
  const [imageModalOpen, setImageModalOpen] = useState(false);

  useEffect(() => {
    if (images?.length) {
      setActiveImage(images[0] ?? null);
    } else {
      setActiveImage(null);
    }
  }, [images]);

  const handleEnterPress = useCallback((event: KeyboardEvent) => {
    if (event.keyCode === 13) {
      setImageModalOpen(true);
    }
  }, []);

  return (
    <>
      <div onClick={(e: MouseEvent) => e.stopPropagation()}>
        <Card style={{ width: "80%" }}>
          <CardHeader>
            <Icon icon={ImagesIcon} />
            {pluralise(images.length, "uploaded image", "uploaded images")}
          </CardHeader>
          <CardContent style={{ textAlign: "center" }}>
            {activeImage ? (
              <div style={{ margin: 3, flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    backgroundColor: "#f8f8f8",
                    borderRadius: 15,
                  }}
                >
                  <img
                    tabIndex={0}
                    src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                    alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                    onClick={() => {
                      setImageModalOpen(true);
                    }}
                    onKeyDown={(e) => handleEnterPress(e)}
                    style={{
                      height: fullImageHeight,
                      width: "100%",
                      objectFit: "contain",
                      borderRadius: 10,
                      cursor: "default",
                    }}
                  />
                </div>
              </div>
            ) : (
              <NoActiveImage
                $imageHeight={`${imageHeight + 40}px`}
                $imageWidth={"100%"}
              >
                <H5>
                  {!images.length
                    ? t("multiimage.upload.none")
                    : "Select an image"}
                </H5>
              </NoActiveImage>
            )}
            <GalleryImages>
              {images && Array.isArray(images) && images.length
                ? images.map((s) => (
                    <GalleryThumbnail
                      src={getFileUrl ? getFileUrl(s) : s.url}
                      key={s._id}
                      onClick={() => setActiveImage(s)}
                      className={cx(activeImage?._id === s._id && "is-active")}
                    />
                  ))
                : null}
            </GalleryImages>
          </CardContent>
          {onRemove ? (
            <CardContent
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "12px 16px",
              }}
            >
              <Button
                onClick={() => {
                  if (activeImage) {
                    onRemove(activeImage._id);
                  }
                }}
                size="small"
                style={{ marginLeft: 0, marginRight: 0 }}
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
              </Button>
            </CardContent>
          ) : null}
        </Card>
      </div>
      {activeImage ? (
        <Modal
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          style={{ maxWidth: "90vw", maxHeight: "90vh" }}
        >
          <ModalHeader> {activeImage.name} </ModalHeader>
          <ModalContent
            scrolling
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 0,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {activeImage && (
              <img
                src={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                alt={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                style={{
                  maxWidth: "90%",
                  maxHeight: "60%",
                  objectFit: "contain",
                  marginBottom: 10,
                  marginTop: 10,
                }}
              />
            )}
          </ModalContent>
          <ModalActions>
            {activeImage && (
              <a
                href={getFileUrl ? getFileUrl(activeImage) : activeImage.url}
                download={activeImage.name || "image.png"}
              >
                <Button color="primary">
                  <Icon icon={DownloadIcon} />
                  Download image
                </Button>
              </a>
            )}
            <Button
              onClick={() => {
                setImageModalOpen(false);
              }}
            >
              Close
            </Button>
          </ModalActions>
        </Modal>
      ) : null}
    </>
  );
};

type MultiImageChooserProps = {
  images: Array<{ _id: string; name: string; value: string; url: string }>;
  onComplete: (files: OnCompleteParams[]) => void;
  deleteFile: (fileId: string) => void;
  getFileUrl?: (file: ChooserGalleryImage) => string;
  forType: string;
  forId: string;
  editing?: boolean;
  maxImages?: number;
};

const MultiImageChooser = ({
  images = [],
  onComplete,
  deleteFile,
  forType,
  forId,
  editing,
  getFileUrl,
  maxImages,
}: MultiImageChooserProps) => {
  if (!editing && !images.length) return null;

  if (!editing) {
    return <Gallery images={images} getFileUrl={getFileUrl} />;
  }

  const onSingleComplete = (
    responseFilename: string,
    filename: string,
    responseDownload: string,
  ) => {
    onComplete([
      {
        savedName: responseFilename,
        fileName: filename,
        signedDownloadRequest: responseDownload,
      },
    ]);
  };

  const hasMaxImages = !!(maxImages && images.length >= maxImages);

  return (
    <div>
      <ImageChooser
        allowMultiple={true}
        onComplete={onSingleComplete}
        onCompleteMultiple={onComplete}
        forType={forType}
        forId={forId}
        disabled={hasMaxImages}
        trigger={
          <FileChooserDropzoneTrigger
            forType={forType}
            forId={forId}
            accept={allowedImageTypesList.join(",")}
            allowMultiple={true}
            onComplete={onSingleComplete}
            onCompleteMultiple={onComplete}
            dropzoneProps={{
              title: "Upload multiple images",
              subtitle: maxImages
                ? `Maximum of ${pluralise(maxImages, "image", "images")}`
                : undefined,
              defaultIcon: ImagesIcon,
              disabled: hasMaxImages,
            }}
          />
        }
      />
      <UploadedFilesTable
        variant="image"
        files={images.map((image) => ({
          id: image._id,
          name: image.name,
          url: getFileUrl ? getFileUrl(image) : image.url,
        }))}
        onRemove={deleteFile}
      />
    </div>
  );
};

export default MultiImageChooser;
